import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "../../../lib/supabase";
import { useAuth } from "../../../hooks/useAuth";

export interface AgentTabRow {
  id: string;
  name: string;
  icon: string | null;
  scope: "personal" | "global";
  owner_user_id: string;
  is_archived: boolean;
}

export interface AgentTab {
  agent: AgentTabRow;
  section: "personal" | "shared";
}

const agentsKey = ["agents", "tabs"] as const;

export function useAgentTabsQuery() {
  return useQuery({
    queryKey: agentsKey,
    queryFn: async (): Promise<AgentTabRow[]> => {
      const { data, error } = await supabase
        .from("agents_view")
        .select("id, name, icon, scope, owner_user_id, is_archived")
        .eq("is_archived", false)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data ?? []).filter(
        (row): row is AgentTabRow =>
          typeof row.id === "string" &&
          typeof row.name === "string" &&
          typeof row.scope === "string" &&
          typeof row.owner_user_id === "string",
      );
    },
    staleTime: 60_000,
  });
}

export function useAgentTabs() {
  const { user } = useAuth();
  const { data, isLoading, error } = useAgentTabsQuery();
  const userId = user?.id ?? null;
  const tabs: AgentTab[] = (data ?? []).map((agent) => ({
    agent,
    section: agent.owner_user_id === userId ? "personal" : "shared",
  }));
  return { tabs, isLoading, error };
}

const ACTIVE_AGENT_KEY = "mannaActiveAgentId";

export function useActiveAgent(tabs: AgentTab[]) {
  const [storedAgentId, setStoredAgentId] = useState<string | null>(() => {
    if (typeof window === "undefined") return null;
    return window.localStorage.getItem(ACTIVE_AGENT_KEY);
  });

  // Derive — don't sync via effect: pick the stored tab if it still
  // exists, otherwise fall back to the first tab.
  const activeAgentId = (() => {
    if (!tabs.length) return null;
    if (storedAgentId && tabs.some((t) => t.agent.id === storedAgentId)) {
      return storedAgentId;
    }
    return tabs[0]!.agent.id;
  })();

  const setActiveAgentId = (next: string | null) => {
    setStoredAgentId(next);
    if (next) window.localStorage.setItem(ACTIVE_AGENT_KEY, next);
    else window.localStorage.removeItem(ACTIVE_AGENT_KEY);
  };

  return { activeAgentId, setActiveAgentId };
}
